import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: localStorage,
    flowType: 'pkce',
    // Set the site URL to our custom domain
    site: window.location.origin,
    // Configure redirect URLs
    redirectTo: `${window.location.origin}/auth/callback`
  }
});

// Function to check Supabase connection
export const checkSupabaseConnection = async () => {
  try {
    // First try to get session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session) {
      return true;
    }

    // If no session, try a public endpoint with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const { data, error } = await supabase
      .from('services')
      .select('count')
      .limit(1)
      .single();

    clearTimeout(timeoutId);

    if (error) {
      console.error('Connection check failed:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Connection check failed:', error);
    return false;
  }
};

// Add a connection recovery function
export const waitForConnection = async (maxAttempts = 3, delayMs = 2000) => {
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    try {
      const isConnected = await checkSupabaseConnection();
      if (isConnected) return true;
    } catch (error) {
      console.error(`Connection attempt ${attempts + 1}/${maxAttempts} failed:`, error);
    }
    
    attempts++;
    console.log(`Connection attempt ${attempts}/${maxAttempts} failed. Retrying...`);
    
    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, delayMs));
  }
  
  return false;
};